import { cloneElement, RefObject, useCallback } from 'react';

import styled from 'styled-components';

import { EmptyState } from '@components/empty-state';
import { IconAiGradient } from '@components/icons/ai-gradient';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { useSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { QuestionAnswer } from 'api/ai-builder/requests';
import { LoadingLabel } from 'api/ai-builder/requests';

import { MessageItem } from './messages';

import { type ChatMessage } from '../hooks';
import { LoadingStateDisplay } from '../loading-state-display';

const DEFAULT_INPUT_ROW_HEIGHT = 72;
const MESSAGE_GAP = pxToRem(12);
const NON_RAIL_TOOL_NAMES = ['enter_plan_mode', 'exit_plan_mode'];

type MessagesContainerProps = {
  messages: ChatMessage[];
  loadingLabel: LoadingLabel | null;
  isBuilding: boolean;
  messagesRef: RefObject<HTMLDivElement>;
  messagesInnerRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  onPauseAutoScroll: () => void;
  inputRowHeight: number;
  onSubmitAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  onPickResource: (selection: { id: string; name: string }) => Promise<void>;
  onAcceptPlan: () => Promise<void>;
  onApproveEnterPlanMode: () => Promise<void>;
  onRejectEnterPlanMode: () => Promise<void>;
  isPlanLoading?: boolean;
  isModeLoading?: boolean;
};

export const MessagesContainer = ({
  messages,
  loadingLabel,
  isBuilding,
  messagesRef,
  messagesInnerRef,
  inputRowHeight,
  onScroll,
  onPauseAutoScroll,
  onSubmitAnswers,
  onPickResource,
  onAcceptPlan,
  onApproveEnterPlanMode,
  onRejectEnterPlanMode,
  isPlanLoading,
  isModeLoading,
}: MessagesContainerProps) => {
  const { t } = useScopedTranslate(locale);
  const { toggle: toggleDescription, isSelected: hasDescriptionOpen } = useSelectionStore();
  const handleScroll = useCallback(() => {
    onScroll();
  }, [onScroll]);

  const bottomOffsetPx = messages.length === 0 ? DEFAULT_INPUT_ROW_HEIGHT : inputRowHeight;
  const bottomOffset = pxToRem(bottomOffsetPx + 24);

  const handleToggleDescription = (id: string) => {
    onPauseAutoScroll();
    toggleDescription(id);
  };

  const isRailToolMessage = (message?: ChatMessage) => {
    if (!message || message.role !== 'tool') return false;
    return !NON_RAIL_TOOL_NAMES.includes(message.toolName ?? '');
  };

  return (
    <StyledMessagesContainer
      ref={messagesRef}
      onScroll={handleScroll}
      style={{
        bottom: bottomOffset,
        justifyContent: messages.length === 0 ? 'center' : 'flex-start',
      }}
    >
      <MessagesInner ref={messagesInnerRef}>
        {messages.length === 0 ? (
          <EmptyStateContainer>
            <EmptyState
              header={t('navigator')}
              content={t('description')}
              orientation="vertical"
              variant="small"
              transparent
              icon={<IconAiGradient />}
              iconShadow={cloneElement(<IconAiGradient />, { opacity: 0.1, size: 240 })}
            />
          </EmptyStateContainer>
        ) : (
          <>
            {messages.map((message, idx) => {
              const isPrevTool =
                idx > 0 && isRailToolMessage(messages[idx - 1]) && isRailToolMessage(message);
              const isNextTool =
                idx < messages.length - 1 &&
                isRailToolMessage(message) &&
                isRailToolMessage(messages[idx + 1]);

              return (
                <MessageItem
                  key={`row-${message.id}`}
                  message={message}
                  index={idx}
                  isPrevTool={isPrevTool}
                  isNextTool={isNextTool}
                  isDescriptionOpen={hasDescriptionOpen(message.id)}
                  onToggleDescription={() => {
                    return handleToggleDescription(message.id);
                  }}
                  onSubmitAnswers={onSubmitAnswers}
                  onPickResource={onPickResource}
                  onAcceptPlan={onAcceptPlan}
                  onApproveEnterPlanMode={onApproveEnterPlanMode}
                  onRejectEnterPlanMode={onRejectEnterPlanMode}
                  isPlanLoading={isPlanLoading}
                  isModeLoading={isModeLoading}
                />
              );
            })}
            <LoadingStateDisplay loadingLabel={loadingLabel} isBuilding={isBuilding} />
          </>
        )}
      </MessagesInner>
    </StyledMessagesContainer>
  );
};

const StyledMessagesContainer = styled(Stack).attrs({
  space: 0,
  justify: 'flex-start',
})({
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  top: pxToRem(16),

  overflowY: 'auto',
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: pxToRem(8),
    height: pxToRem(8),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.color.uiTrans10,
    borderRadius: pxToRem(8),
  },
});

const MessagesInner = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: MESSAGE_GAP,
});

const EmptyStateContainer = styled.div({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const locale = {
  en: {
    navigator: 'Navigator',
    description: 'Talk to our AI Builder to create\n or edit your workflow',
  },
};
